/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1671667200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Dec 22 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "userflow",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "A note on \"groups\" and \"memberships\"",
          "depth": 1,
          "id": "a-note-on-groups-and-memberships"
        }, {
          "value": "Pruning memberships",
          "depth": 1,
          "id": "pruning-memberships"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      em: "em",
      img: "img",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      strong: "strong",
      a: "a",
      blockquote: "blockquote"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "Upon selecting Userflow as your destination you will be prompted to\ninput your Userflow API key."
      }), "\n", _jsxs(_components.p, {
        children: ["You can locate this API key within your Userflow account by navigating to ", _jsx(_components.em, {
          children: "Settings > API"
        }), ":"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-userflow-setup.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing the following Userflow objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Users"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Groups"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": pushes new objects to Userflow ", _jsx(_components.em, {
            children: "and"
          }), " updates fields that change in your warehouse."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": only updates particular fields on existing objects in Userflow. It does ", _jsx(_components.em, {
            children: "not"
          }), " add new objects."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Records can be matched from your source to your Userflow workspace by the ", _jsx(_components.strong, {
          children: "user ID"
        }), " or ", _jsx(_components.strong, {
          children: "group ID"
        }), ", depending on the object you are syncing."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-userflow-record-matching.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You may also sync columns from your source to Userflow's object properties. You have the option\nof mapping to attributes that already exist on the Userflow object, or to create new attributes for current\nand future use."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-userflow-field-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["*NOTE: Aside from ", _jsx(_components.code, {
            children: "groups"
          }), " and ", _jsx(_components.code, {
            children: "memberships"
          }), " on ", _jsx(_components.code, {
            children: "User"
          }), " objects, all other fields will be associated with the ", _jsx(_components.code, {
            children: "Group"
          }), " or ", _jsx(_components.code, {
            children: "User"
          }), " object's ", _jsx(_components.code, {
            children: "attributes"
          }), " field within Userflow."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "a-note-on-groups-and-memberships",
        children: "A note on \"groups\" and \"memberships\""
      }), "\n", _jsxs(_components.p, {
        children: ["The Userflow API allows you to create/update both groups and memberships through creating/updating\na user by including the appropriate ", _jsx(_components.code, {
          children: "groups"
        }), "/", _jsx(_components.code, {
          children: "memberships"
        }), " as fields on the user object in the\nrequest payload."]
      }), "\n", _jsxs(_components.p, {
        children: ["However, please be aware that their inclusion is not required and at most only one of either ", _jsx(_components.code, {
          children: "groups"
        }), "\nor ", _jsx(_components.code, {
          children: "memberships"
        }), " can be set on a user per request. If your intention is only to sync group information,\nit may be best to sync ", _jsx(_components.code, {
          children: "Group"
        }), " objects instead."]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information on ", _jsx(_components.code, {
          children: "groups"
        }), " and ", _jsx(_components.code, {
          children: "memberships"
        }), ", including how they are properly formatted on a ", _jsx(_components.code, {
          children: "User"
        }), " object,\nplease consult the ", _jsx(_components.a, {
          href: "https://userflow.com/docs/api#create-or-update-a-user",
          children: "Userflow API documentation"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Formatting ", _jsx(_components.code, {
            children: "groups"
          }), " for a ", _jsx(_components.code, {
            children: "User"
          }), ":"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-userflow-create-group-on-user.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Formatting ", _jsx(_components.code, {
            children: "memberships"
          }), " for a ", _jsx(_components.code, {
            children: "User"
          }), ":"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-userflow-create-membership-on-user.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "pruning-memberships",
        children: "Pruning memberships"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch also supports the ability to prune memberships of a user upon sync through the ", _jsx(_components.code, {
          children: "prune_memberships"
        }), "\nproperty in the request payload. As per the Userflow API documentation:"]
      }), "\n", _jsxs(_components.blockquote, {
        children: ["\n", _jsx(_components.p, {
          children: _jsxs(_components.em, {
            children: ["\"By default, the API will only update the memberships/groups that's included in the request. Existing memberships that aren't included will not be removed, unless you set ", _jsx(_components.code, {
              children: "prune_memberships"
            }), " to ", _jsx(_components.code, {
              children: "true"
            }), ". Only set ", _jsx(_components.code, {
              children: "prune_memberships"
            }), " to ", _jsx(_components.code, {
              children: "true"
            }), ", if the groups or memberships list is set and contains ", _jsx(_components.em, {
              children: "all"
            }), " the groups the user belongs to. When a membership is deleted, the group itself is left intact.\""]
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When either ", _jsx(_components.code, {
          children: "groups"
        }), " or ", _jsx(_components.code, {
          children: "memberships"
        }), " are chosen to be synced to the destination, Hightouch will give you\nthe option to toggle membership pruning on or off."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-userflow-pruning-memberships.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "*NOTE: By default, membership pruning is disabled."
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
